import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const EMAIL = "Eddy@theaccountantgroup.com.au";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    if (!isEmail(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success("Thank you for your enquiry. We'll be in touch shortly.");
        setForm({ name: "", email: "", phone: "", message: "" });
      } else {
        toast.error("Something went wrong. Please try again, or call 0459 646 667.");
      }
    } catch (err) {
      toast.error("Network error. Please try again, or call 0459 646 667.");
    } finally {
      setSubmitting(false);
    }
  };

  const details = [
    { icon: Phone, label: "Phone", value: "0459 646 667", href: "tel:+61459646667", testId: "contact-phone" },
    { icon: Mail, label: "Email", value: EMAIL, href: `mailto:${EMAIL}`, testId: "contact-email" },
    { icon: MapPin, label: "Location", value: "Bankstown, NSW — Australia-wide", href: null, testId: "contact-location" },
  ];

  const inputCls =
    "w-full bg-transparent border-b border-white/15 focus:border-[#c9a84c] py-3 font-body text-[#f8f9fa] placeholder:text-[#5c6066] outline-none transition-colors duration-300";

  return (
    <section id="contact" className="relative py-24 md:py-40 bg-[#0c0c12]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-4 mb-16">
          <span className="font-body text-xs tracking-[0.3em] uppercase text-[#c9a84c]">04 — Contact</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left: heading + details */}
          <div className="lg:col-span-5">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-light tracking-tight text-[#f8f9fa] text-3xl sm:text-4xl lg:text-5xl leading-tight"
            >
              Book a private consultation.
            </motion.h2>
            <p className="font-body text-[#9ba1a6] font-light mt-6 leading-relaxed max-w-md">
              A confidential first conversation, no obligation. Tell us a little about your
              situation and we'll be in touch personally.
            </p>

            <div className="mt-14 space-y-8">
              {details.map((d) => {
                const Icon = d.icon;
                const content = (
                  <div className="flex items-start gap-5 group" data-testid={d.testId}>
                    <span className="mt-1 shrink-0 h-11 w-11 grid place-items-center border border-[#c9a84c]/30 rounded-full group-hover:bg-[#c9a84c] transition-colors duration-500">
                      <Icon size={18} className="text-[#c9a84c] group-hover:text-[#0a0a0f] transition-colors duration-500" strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="font-body text-xs tracking-[0.2em] uppercase text-[#9ba1a6] mb-1">{d.label}</p>
                      <p className="font-body text-lg text-[#f8f9fa] group-hover:text-[#e8d9a8] transition-colors duration-300">{d.value}</p>
                    </div>
                  </div>
                );
                return d.href ? (
                  <a key={d.label} href={d.href}>{content}</a>
                ) : (
                  <div key={d.label}>{content}</div>
                );
              })}
            </div>
          </div>

          {/* Right: form */}
          <motion.form
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-[#12121a] border border-white/5 p-8 md:p-12"
          >
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className="font-body text-xs tracking-[0.2em] uppercase text-[#9ba1a6]">Name</label>
                <input data-testid="contact-input-name" value={form.name} onChange={update("name")} placeholder="Your full name" className={inputCls} />
              </div>
              <div>
                <label className="font-body text-xs tracking-[0.2em] uppercase text-[#9ba1a6]">Email</label>
                <input data-testid="contact-input-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" className={inputCls} />
              </div>
            </div>
            <div className="mt-8">
              <label className="font-body text-xs tracking-[0.2em] uppercase text-[#9ba1a6]">Phone (optional)</label>
              <input data-testid="contact-input-phone" value={form.phone} onChange={update("phone")} placeholder="04XX XXX XXX" className={inputCls} />
            </div>
            <div className="mt-8">
              <label className="font-body text-xs tracking-[0.2em] uppercase text-[#9ba1a6]">How can we help?</label>
              <textarea data-testid="contact-input-message" value={form.message} onChange={update("message")} rows={4} placeholder="A few words about what you need…" className={`${inputCls} resize-none`} />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-button"
              className="group mt-12 w-full sm:w-auto inline-flex items-center justify-center gap-3 font-body text-sm tracking-wide px-9 py-4 rounded-full bg-[#c9a84c] text-[#0a0a0f] font-medium overflow-hidden relative disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="relative z-10">{submitting ? "Sending…" : "Send Enquiry"}</span>
              <ArrowUpRight size={18} className="relative z-10 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              <span className="absolute inset-0 bg-[#e0c268] scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
